import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePromptStore } from '@/stores/promptStore';
import { useAuthStore } from '@/stores/authStore';
import { PromptEditor } from './PromptEditor';
import type { Prompt } from '@/types/prompt';
import { toast } from 'sonner';

export function PromptManagement() {
  const { prompts, isLoading, loadPrompts, deletePrompt } = usePromptStore();
  const { canCreatePrompt, canEditPrompt, canDeletePrompt } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const handleCreate = () => {
    setEditingPrompt(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setIsEditorOpen(true);
  };

  const handleDelete = async (prompt: Prompt) => {
    if (!confirm(`确定要删除 Prompt "${prompt.title}" 吗？`)) {
      return;
    }

    try {
      await deletePrompt(prompt.id);
      toast.success('Prompt 已删除');
    } catch (error) {
      toast.error('删除失败');
    }
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingPrompt(null);
    loadPrompts(); // 重新加载列表
  };

  const filteredPrompts = prompts.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.title.toLowerCase().includes(query) ||
      p.description.toLowerCase().includes(query) ||
      p.category.toLowerCase().includes(query)
    );
  });

  return (
    <div className="relative space-y-6">
      {/* 头部操作栏 */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索 Prompt..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        {canCreatePrompt() && (
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            创建 Prompt
          </Button>
        )}
      </div>

      {/* 统计信息 */}
      <div className="text-sm text-muted-foreground">
        共 {filteredPrompts.length} 个 Prompt
      </div>

      {/* Prompt 列表 */}
      {isLoading ? (
        <div className="text-center py-12">加载中...</div>
      ) : filteredPrompts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQuery ? '没有找到匹配的 Prompt' : '还没有任何 Prompt'}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPrompts.map((prompt) => (
            <div
              key={prompt.id}
              className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold truncate">{prompt.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {prompt.permission}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {prompt.category}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {prompt.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>ID: {prompt.id}</span>
                    {prompt.tags && prompt.tags.length > 0 && (
                      <>
                        <span>•</span>
                        <span>标签: {prompt.tags.join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEditPrompt(prompt.createdBy) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(prompt)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {canDeletePrompt() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(prompt)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Prompt 编辑器对话框 */}
      <PromptEditor
        prompt={editingPrompt}
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
      />
    </div>
  );
}

